import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  AvailabilityRange,
  SupplierNetworkConfigPanel,
  useCreateSupplierNetworkConfigMutation,
  useLazyGetSupplierNetworkConfigQuery,
} from "../../../../redux/apiSpecifications/apiFesf";
import { useTranslation } from "react-i18next";
import { InputTextStyled } from "../../../../style/Input";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { Popconfirm, Tooltip } from "antd";
import { ButtonConfirm } from "../../../Layout/Buttons";
import { LoadingFullScreenWrapper } from "../../../../style/DashbordWidgetWrappers";
import { IconLoading } from "../../../../config/icons";
import { IoMdHelpCircleOutline } from "react-icons/io";
import { useApplicationError } from "../../../../hooks/useApplicationError";

//Styled components
const ConfigurationPanelWrapper = styled.div`
  display: flex;
  aòign-items: flex-start;
  justify-content: center;
  padding: 1em;

  .wrapper-content {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: center;
    padding: 2em;
    border-radius: 0.5em;
    overflow: hidden;
    box-shadow: 0 0 5px #aaa;
    background-color: #fff;
    gap: 2em 10em;
  }

  .buttons-wrapper {
    width: 100%;
    margin-top: 2em;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1em;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  .inner-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .header {
      text-align: center;
      font-size: 1em;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 2em;
    }

    th {
      font-weight: normal;
    }

    td {
      .square-text {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding-right: 1em;
      }
    }
  }

  .input-percentage {
    input::-webkit-inner-spin-button,
    input::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input {
      -moz-appearance: textfield;
      padding-right: 1.8em;
    }
  }
`;

const ThresoldsWrapper = styled(Wrapper)`
  grid-area: thresolds;

  .ant-form-item {
    width: 5em;
  }

  .square-C1,
  .square-C2,
  .square-C3,
  .square-C4 {
    width: 3em;
    height: 2em;
    border-radius: 5px;
    margin-right: 1em;
  }

  .square-C1 {
    background-color: #002e55;
  }

  .square-C2 {
    background-color: #ef7c00;
  }

  .square-C3 {
    background-color: #ffcc00;
  }

  .square-C4 {
    background-color: #64b32c;
  }
`;

const VariablesWrapper = styled(Wrapper)`
  grid-area: variables;

  .ant-form-item {
    width: 5em;
    margin-left: 2em;
  }

  .subtitle {
    text-transform: uppercase;
    font-size: 0.9em;
    padding-top: 2em;
  }

  .margin-left {
    padding-left: 2em;
  }
`;

interface IConfigurationPanelProps {}

const ConfigurationPanel = (props: IConfigurationPanelProps) => {
  const { t } = useTranslation();
  const { getAuthorization } = useAuthorization();
  const { showApplicationError } = useApplicationError();

  const [data, setData] = useState<SupplierNetworkConfigPanel | undefined>();
  const [status, setStatus] = useState<"idle" | "loading" | "saving" | "error">(
    "idle"
  );
  const [popconfirmSave, setPopconfirmSave] = useState<boolean>(false);

  // API
  const [loadConfiguration] = useLazyGetSupplierNetworkConfigQuery();
  const [updateConfiguration] = useCreateSupplierNetworkConfigMutation();

  const ranges = useMemo(() => {
    if (data === undefined) return {};

    return {
      C1: data.availabilityRangeConfig?.find(
        (r) => r.availabilityRangeType === "C1"
      ),
      C2: data.availabilityRangeConfig?.find(
        (r) => r.availabilityRangeType === "C2"
      ),
      C3: data.availabilityRangeConfig?.find(
        (r) => r.availabilityRangeType === "C3"
      ),
      C4: data.availabilityRangeConfig?.find(
        (r) => r.availabilityRangeType === "C4"
      ),
    };
  }, [data]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setStatus("loading");
    const { data, isError, isSuccess } = await loadConfiguration({
      authorization: await getAuthorization(),
    });

    if (isSuccess) {
      const _data = data as SupplierNetworkConfigPanel;
      const formattedDate = {
        ..._data,
        availabilityRangeConfig: _data.availabilityRangeConfig?.map((r) => {
          return {
            availabilityRangeType: r.availabilityRangeType,
            rangeFrom: Math.floor((r.rangeFrom || 0) * 1000) / 10,
            rangeTo: Math.round((r.rangeTo || 0) * 1000) / 10,
          };
        }),
      };

      setData(formattedDate);
    } else if (isError) {
      setStatus("error");
      // to do
      return;
    }

    setStatus("idle");
  };

  const handleChange = (val: any, type: string, rangeField: string) => {
    if (Number.isNaN(val)) return;

    let updatedData: SupplierNetworkConfigPanel = {
      ...data,
    };

    if (type === "C1" || type === "C2" || type === "C3" || type === "C4") {
      const parsedVal = parseFloat(val);
      if (parsedVal < 0 || parsedVal > 100) return;

      const step = 0.1;
      let C1 =
        updatedData.availabilityRangeConfig?.find(
          (r) => r.availabilityRangeType === "C1"
        ) || ({} as AvailabilityRange);
      let C2 =
        updatedData.availabilityRangeConfig?.find(
          (r) => r.availabilityRangeType === "C2"
        ) || ({} as AvailabilityRange);

      let C3 =
        updatedData.availabilityRangeConfig?.find(
          (r) => r.availabilityRangeType === "C3"
        ) || ({} as AvailabilityRange);

      let C4 =
        updatedData.availabilityRangeConfig?.find(
          (r) => r.availabilityRangeType === "C4"
        ) || ({} as AvailabilityRange);

      if (type === "C1") {
        if (rangeField == "rangeFrom") C1.rangeFrom = parsedVal;
        else if (rangeField == "rangeTo") {
          C1.rangeTo = parsedVal;
          C2.rangeFrom = parsedVal + step;
        }
      }

      if (type === "C2") {
        if (rangeField == "rangeFrom") {
          C2.rangeFrom = parsedVal;
          C1.rangeTo = parsedVal - step;
        } else if (rangeField == "rangeTo") {
          C2.rangeTo = parsedVal;
          C3.rangeFrom = parsedVal + step;
        }
      }

      if (type === "C3") {
        if (rangeField == "rangeFrom") {
          C3.rangeFrom = parsedVal;
          C2.rangeTo = parsedVal - step;
        } else if (rangeField == "rangeTo") {
          C3.rangeTo = parsedVal;
          C4.rangeFrom = parsedVal + step;
        }
      }

      if (type === "C4") {
        if (rangeField == "rangeFrom") {
          C4.rangeFrom = parsedVal;
          C3.rangeTo = parsedVal - step;
        } else if (rangeField == "rangeTo") {
          C4.rangeTo = parsedVal;
        }
      }

      updatedData.availabilityRangeConfig = [C1, C2, C3, C4];
    } else {
      const parsedVal = parseFloat(val);
      if (parsedVal < 0) return;

      (updatedData[type as keyof SupplierNetworkConfigPanel] as number) =
        parsedVal;
    }

    setData(updatedData);
  };

  const handleSaveStatus = async () => {
    setPopconfirmSave(false);

    setStatus("saving");

    const supplierNetworkConfigPanel = {
      ...data,
      availabilityRangeConfig: data?.availabilityRangeConfig?.map((r) => {
        return {
          availabilityRangeType: r.availabilityRangeType,
          rangeFrom: (r?.rangeFrom || 0) / 100,
          rangeTo: (r?.rangeTo || 0) / 100,
        };
      }),
    };

    updateConfiguration({
      authorization: await getAuthorization(),
      supplierNetworkConfigPanel,
    })
      .unwrap()
      .then(loadData)
      .catch((error) => {
        showApplicationError(error);
      })
      .finally(() => setStatus("idle"));
  };

  const disableSaveButton = useMemo(() => {
    if (!data) return true;

    if (
      !data.availabilityRangeConfig ||
      data.availabilityRangeConfig.length !== 4
    ) {
      return true;
    }

    for (const item of data.availabilityRangeConfig) {
      if (
        item.rangeFrom === null ||
        item.rangeFrom === undefined ||
        Number.isNaN(item.rangeFrom) ||
        item.rangeTo === null ||
        item.rangeTo === undefined ||
        Number.isNaN(item.rangeTo)
      ) {
        return true;
      }

      if (item.rangeTo <= item.rangeFrom) {
        return true;
      }
    }

    const numericFields: (keyof SupplierNetworkConfigPanel)[] = [
      "leaveforewarning",
      "smallstudiesthreshold",
      "leavethreshold",
      "assignmentdecreasestartdays",
      "assignmentstopstartdays",
      "assignmentpercentagedecrease",
    ];

    for (const field of numericFields) {
      const value = data[field];

      if (value === null || value === undefined || Number.isNaN(value)) {
        return true;
      }
    }

    return false;
  }, [data]);

  return (
    <>
      {["loading", "saving"].includes(status) && (
        <LoadingFullScreenWrapper>
          <div className="box">{IconLoading}</div>
        </LoadingFullScreenWrapper>
      )}

      <ConfigurationPanelWrapper>
        <div className="wrapper-content">
          <ThresoldsWrapper>
            <div className="inner-wrapper">
              <div className="header">
                {t("configuration-panel-thresolds-header")}
              </div>
              <div className="content">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>{t("configuration-panel-thresolds-table-from")}</th>
                      <th>{t("configuration-panel-thresolds-table-to")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["C1", "C2", "C3", "C4"].map((rangeType) => (
                      <tr key={rangeType}>
                        <td>
                          <div className="square-text">
                            <div className={`square-${rangeType}`}></div>
                            <div className="label">
                              {t(`range-availability-${rangeType}`)}
                            </div>
                          </div>
                        </td>
                        <td>
                          <InputTextStyled
                            type="number"
                            label=""
                            value={
                              ranges[rangeType as keyof typeof ranges]
                                ?.rangeFrom
                            }
                            onChange={(val) =>
                              handleChange(val, rangeType, "rangeFrom")
                            }
                          />
                        </td>
                        <td>
                          <InputTextStyled
                            type="number"
                            label=""
                            value={
                              ranges[rangeType as keyof typeof ranges]?.rangeTo
                            }
                            onChange={(val) =>
                              handleChange(val, rangeType, "rangeTo")
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ThresoldsWrapper>

          <VariablesWrapper>
            <div className="inner-wrapper">
              <div className="header">
                {t("configuration-panel-variables-header")}
              </div>
              <div className="content">
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <Tooltip
                          title={t("TOOLTIP.configuration-panel-param-1")}
                        >
                          <span>
                            <IoMdHelpCircleOutline />
                          </span>
                        </Tooltip>
                        {t("LABELS.leave_forewarning")}
                      </td>
                      
                      <td>
                        <InputTextStyled
                          type="number"
                          label=""
                          value={data?.leaveforewarning}
                          onChange={(val) =>
                            handleChange(val, "leaveforewarning", "")
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="subtitle">
                        {t("configuration-panel-param-title-1")}
                      </td>
                    </tr>
                    <tr>
                      <td className="margin-left">
                        <Tooltip
                          title={t("TOOLTIP.configuration-panel-param-2")}
                        >
                          <span>
                            <IoMdHelpCircleOutline />
                          </span>
                        </Tooltip>
                        {t("LABELS.small_studies_threshold")}
                      </td>
                      <td>
                        <InputTextStyled
                          type="number"
                          label=""
                          value={data?.smallstudiesthreshold}
                          onChange={(val) =>
                            handleChange(val, "smallstudiesthreshold", "")
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="margin-left">
                        <Tooltip
                          title={t("TOOLTIP.configuration-panel-param-3")}
                        >
                          <span>
                            <IoMdHelpCircleOutline />
                          </span>
                        </Tooltip>
                        {t("LABELS.leave_threshold")}
                      </td>
                      <td>
                        <InputTextStyled
                          type="number"
                          label=""
                          value={data?.leavethreshold}
                          onChange={(val) =>
                            handleChange(val, "leavethreshold", "")
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="subtitle">
                        {t("configuration-panel-param-title-2")}
                      </td>
                    </tr>
                    <tr>
                      <td className="margin-left">
                        <Tooltip
                          title={t("TOOLTIP.configuration-panel-param-4")}
                        >
                          <span>
                            <IoMdHelpCircleOutline />
                          </span>
                        </Tooltip>
                        {t("LABELS.assignment_decrease_start_days")}
                      </td>
                      <td>
                        <InputTextStyled
                          type="number"
                          label=""
                          value={data?.assignmentdecreasestartdays}
                          onChange={(val) =>
                            handleChange(val, "assignmentdecreasestartdays", "")
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="margin-left">
                        <Tooltip
                          title={t("TOOLTIP.configuration-panel-param-5")}
                        >
                          <span>
                            <IoMdHelpCircleOutline />
                          </span>
                        </Tooltip>
                        {t("LABELS.assignment_stop_start_days")}
                      </td>
                      <td>
                        <InputTextStyled
                          type="number"
                          label=""
                          value={data?.assignmentstopstartdays}
                          onChange={(val) =>
                            handleChange(val, "assignmentstopstartdays", "")
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="margin-left">
                        <Tooltip
                          title={t("TOOLTIP.configuration-panel-param-6")}
                        >
                          <span>
                            <IoMdHelpCircleOutline />
                          </span>
                        </Tooltip>
                        {t("LABELS.assignment_percentage_decrease")}
                      </td>
                      <td className="input-percentage">
                        <InputTextStyled
                          type="number"
                          label=""
                          value={data?.assignmentpercentagedecrease}
                          onChange={(val) =>
                            handleChange(
                              val,
                              "assignmentpercentagedecrease",
                              ""
                            )
                          }
                        />
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              top: "-1.8em",
                              right: "10px",
                            }}
                          >
                            %
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </VariablesWrapper>

          <div className="buttons-wrapper">
            <Popconfirm
              placement="top"
              icon={null}
              open={popconfirmSave}
              title={t("configuration-panel-button-save-confirm")!}
              onConfirm={handleSaveStatus}
              onCancel={() => setPopconfirmSave(false)}
              okText={t("switch-yes")!}
              cancelText={t("switch-no")!}
            >
              <div>
                <ButtonConfirm
                  onClick={() => setPopconfirmSave(true)}
                  disabled={disableSaveButton}
                >
                  {t("button-save")!}
                </ButtonConfirm>
              </div>
            </Popconfirm>
          </div>
        </div>
      </ConfigurationPanelWrapper>
    </>
  );
};

export default ConfigurationPanel;
